import { components, paths } from "@/api-schema";
import {
  PathsV1CasaviContactBlacklistSearchPostParametersQueryOrderByDirection,
  PathsV1CasaviContactBlacklistSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useCasaviContactBlacklistDeleteMutation } from "@/common/api/casavi/contact-blacklist/use-casavi-contact-blacklist-delete.mutation";
import { useCasaviContactBlacklistSearchQuery } from "@/common/api/casavi/contact-blacklist/use-casavi-contact-blacklist-search.query";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { TableFeatures } from "@/components/data-table/types";
import i18next from "i18next";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { createBasePredefinedTable } from "./_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "./_components/base-predefined-table.types";
import { casaviContactBlacklistTableDefaultColumns } from "./casavi-contact-blacklist-table-columns";

type CasaviContactBlacklist = components["schemas"]["CasaviContactBlacklist"] & RowWithIdentifier;
type CasaviContactBlacklistSorting = NonNullable<
  paths["/v1/casavi-contact-blacklist/search"]["post"]["parameters"]["query"]["orderBy"]
>;
type CasaviContactBlacklistQueryParams = paths["/v1/casavi-contact-blacklist/search"]["post"]["parameters"]["query"];
type CasaviContactBlacklistRequestBody =
  paths["/v1/casavi-contact-blacklist/search"]["post"]["requestBody"]["content"]["application/json"];

const DEFAULT_COLUMNS = [
  casaviContactBlacklistTableDefaultColumns.columnElement,
  casaviContactBlacklistTableDefaultColumns.columnType,
  casaviContactBlacklistTableDefaultColumns.columnValue,
  casaviContactBlacklistTableDefaultColumns.columnCaseSensitive,
];

const CASAVI_CONTACT_BLACKLIST_TABLE_CONFIG: PredefinedTableConfig<CasaviContactBlacklistSorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1CasaviContactBlacklistSearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1CasaviContactBlacklistSearchPostParametersQueryOrderByField.Value,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: i18next.t("casavi:SearchValue"),
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: false,
    } as TableFeatures,
  } as const,
  defaultRowActions: {
    showEdit: true,
    showDelete: true,
    showView: false,
  } as const,
  sortingMapping: {
    element: ["element"],
    type: ["type"],
    value: ["value"],
    caseSensitive: ["caseSensitive"],
    createdAt: ["createdAt"],
    updatedAt: ["updatedAt"],
  } as const,
} as const;

const CASAVI_CONTACT_BLACKLIST_TABLE_QUERY_HOOKS: DataFetchHooks<
  CasaviContactBlacklist,
  CasaviContactBlacklistQueryParams,
  CasaviContactBlacklistRequestBody
> = {
  useSearchQuery: useCasaviContactBlacklistSearchQuery,
  useDeleteMutation: useCasaviContactBlacklistDeleteMutation,
};

const BaseCasaviContactBlacklistTable = createBasePredefinedTable({
  config: CASAVI_CONTACT_BLACKLIST_TABLE_CONFIG,
  queryHooks: CASAVI_CONTACT_BLACKLIST_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function CasaviContactBlacklistTable(
  props: BasePredefinedTableProps<
    CasaviContactBlacklist,
    CasaviContactBlacklistSorting,
    CasaviContactBlacklistQueryParams,
    CasaviContactBlacklistRequestBody
  >,
) {
  const { t } = useTranslation(["casavi", "common"]);
  const deleteMutation = useCasaviContactBlacklistDeleteMutation();

  const handleDelete = useCallback(
    async (contact: CasaviContactBlacklist) => {
      const contactIdentifier = t("casavi:ValueLabel", { value: contact.value });
      await toastMutationStatus({
        mutationFn: () => deleteMutation.mutateAsync({ uuid: contact.uuid }),
        successMessage: t("casavi:ContactBlacklistRemoved", { identifier: contactIdentifier }),
        errorMessage: t("casavi:ErrorRemovingContactBlacklist", { identifier: contactIdentifier }),
      });
    },
    [deleteMutation, t],
  );

  const { name = "casavi-contact-blacklist-table", showCreateButton = true, ...restProps } = props;

  return (
    <BaseCasaviContactBlacklistTable
      searchPlaceholder={t("casavi:SearchValue")}
      name={name}
      showCreateButton={showCreateButton}
      onDelete={handleDelete}
      confirmActions={["delete"]}
      confirmLabels={{
        delete: {
          title: t("casavi:RemoveContactFromBlacklist"),
          text: t("common:ActionCannotBeUndone"),
          confirmButton: t("common:YesRemove"),
          cancelButton: t("common:Cancel"),
        },
      }}
      {...restProps}
    />
  );
}
